import { useRef, useState, useEffect } from 'react';
import { jsPDF } from 'jspdf';
import 'react-advanced-cropper/dist/style.css';
import '../styles/crop.css';
import { v4 as uuidv4 } from 'uuid';
import AdvancedImageCropper from './TestImagesToPDFCropper';

const TestImagestoPDF = () => {
  const [images, setImages] = useState([]);
  const [fileName, setFileName] = useState('captured-images');
  const [croppingImage, setCroppingImage] = useState(null);
  const cameraRef = useRef();

  useEffect(() => {
    navigator.mediaDevices.getUserMedia({ video: true }).then((stream) => {
      if (cameraRef.current) {
        cameraRef.current.srcObject = stream;
      }
    });
  }, []);

  const handleCapture = () => {
    const video = cameraRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    const imageSrc = canvas.toDataURL('image/jpeg');
    setImages((prev) => [...prev, { id: uuidv4(), src: imageSrc, rotation: 0 }]);
  };

  const handleCropComplete = (croppedDataUrl) => {
    setImages((prev) =>
      prev.map((img) =>
        img.id === croppingImage.id ? { ...img, src: croppedDataUrl } : img
      )
    );
    setCroppingImage(null);
  };

  const handleDelete = (id) => {
    setImages((prev) => prev.filter((img) => img.id !== id));
  };

  const rotateImage = (id) => {
    setImages((prev) =>
      prev.map((img) =>
        img.id === id ? { ...img, rotation: (img.rotation + 90) % 360 } : img
      )
    );
  };

  const handleDragStart = (e, index) => {
    e.dataTransfer.setData('dragIndex', index);
  };

  const handleDrop = (e, dropIndex) => {
    const dragIndex = parseInt(e.dataTransfer.getData('dragIndex'));
    if (dragIndex === dropIndex) return;
    const reordered = [...images];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    setImages(reordered);
  };

  const generatePDF = async () => {
    if (images.length === 0) return;
    const pdf = new jsPDF();
    for (let i = 0; i < images.length; i++) {
      const img = new Image();
      img.src = images[i].src;
      await new Promise((resolve) => {
        img.onload = () => {
          const pageWidth = pdf.internal.pageSize.getWidth();
          const pageHeight = pdf.internal.pageSize.getHeight();

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          let [cw, ch] = [img.width, img.height];
          if (images[i].rotation === 90 || images[i].rotation === 270) {
            [cw, ch] = [img.height, img.width];
          }
          canvas.width = cw;
          canvas.height = ch;

          ctx.save();
          if (images[i].rotation === 90) {
            ctx.translate(cw, 0);
            ctx.rotate((90 * Math.PI) / 180);
          } else if (images[i].rotation === 180) {
            ctx.translate(cw, ch);
            ctx.rotate((180 * Math.PI) / 180);
          } else if (images[i].rotation === 270) {
            ctx.translate(0, ch);
            ctx.rotate((270 * Math.PI) / 180);
          }
          ctx.drawImage(img, 0, 0);
          ctx.restore();

          const finalImg = canvas.toDataURL('image/jpeg');
          if (i > 0) pdf.addPage();
          const ratio = Math.min(pageWidth / cw, pageHeight / ch);
          const w = cw * ratio;
          const h = ch * ratio;
          const x = (pageWidth - w) / 2;
          const y = (pageHeight - h) / 2;
          pdf.addImage(finalImg, 'JPEG', x, y, w, h);
          resolve();
        };
      });
    }
    pdf.save(`${fileName || 'captured-images'}.pdf`);
  };

  return (
    <div className="container my-4">
      <h3>📸 Capture, Crop, Rotate, and Export to PDF</h3>

      <div className="mb-3">
        <label className="form-label">PDF File Name</label>
        <input
          type="text"
          className="form-control"
          value={fileName}
          onChange={(e) => setFileName(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <video
          ref={cameraRef}
          autoPlay
          className="rounded border w-100 mb-2"
          style={{ maxHeight: 1000, objectFit: 'cover' }}
        />
        <button className="btn btn-primary w-100" onClick={handleCapture}>
          Capture from Camera
        </button>
      </div>

      <div className="d-flex flex-wrap gap-3">
        {images.map((img, index) => (
          <div
            key={img.id}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleDrop(e, index)}
            className="p-2 border rounded text-center bg-light"
            style={{ width: 140 }}
          >
            <img
              src={img.src}
              alt=""
              className="img-fluid"
              style={{
                transform: `rotate(${img.rotation || 0}deg)`,
                maxHeight: 100,
              }}
            />
            <div className="btn-group btn-group-sm mt-2 w-100">
              <button className="btn btn-outline-primary" onClick={() => setCroppingImage(img)}>
                Crop
              </button>
              <button className="btn btn-outline-secondary" onClick={() => rotateImage(img.id)}>
                ⟳
              </button>
              <button className="btn btn-outline-danger" onClick={() => handleDelete(img.id)}>
                ✖
              </button>
            </div>
          </div>
        ))}
      </div>

      <button className="btn btn-success mt-4 w-100" onClick={generatePDF}>
        Generate PDF
      </button>

      {croppingImage && (
        <div className="modal show d-block bg-dark bg-opacity-75">
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Crop Image</h5>
                <button type="button" className="btn-close" onClick={() => setCroppingImage(null)}></button>
              </div>
              <div className="modal-body">
                <AdvancedImageCropper
                  image={croppingImage.src}
                  onCropComplete={handleCropComplete}
                />
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setCroppingImage(null)}>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestImagestoPDF;
